<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import baileysChannel from 'dashboard/api/channel/baileysChannel';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const { hasPermission, isAdministrator } = usePermissions();
const canManage = ref(
  isAdministrator.value || hasPermission('inbox.manage_session')
);

const checkPhone = ref('');
const checkResult = ref(null);
const blocklist = ref([]);
const blocklistLoaded = ref(false);
const contactJid = ref('');
const contactStatus = ref(null);
const blockJid = ref('');
const isLoading = ref({});

const handleCheckNumber = async () => {
  const phone = checkPhone.value.replace(/\D/g, '');
  if (phone.length < 10) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.INVALID_PHONE'));
    return;
  }
  isLoading.value = { ...isLoading.value, check: true };
  try {
    const { data } = await baileysChannel.checkNumber(props.inbox.id, phone);
    checkResult.value = data;
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, check: false };
  }
};

const fetchBlocklist = async () => {
  isLoading.value = { ...isLoading.value, blocklist: true };
  try {
    const { data } = await baileysChannel.blocklist(props.inbox.id);
    blocklist.value = data.blocked || [];
    blocklistLoaded.value = true;
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCKLIST_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, blocklist: false };
  }
};

const handleBlock = async action => {
  const jid = blockJid.value.trim();
  if (!jid) return;
  isLoading.value = { ...isLoading.value, block: true };
  try {
    await baileysChannel.blockContact(props.inbox.id, jid, action);
    useAlert(
      t(`INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.${action.toUpperCase()}_DONE`)
    );
    blockJid.value = '';
    if (blocklistLoaded.value) await fetchBlocklist();
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCK_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, block: false };
  }
};

const handleContactStatus = async () => {
  const jid = contactJid.value.trim();
  if (!jid) return;
  isLoading.value = { ...isLoading.value, contactStatus: true };
  try {
    const { data } = await baileysChannel.contactStatus(props.inbox.id, jid);
    contactStatus.value = data;
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CONTACT_STATUS_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, contactStatus: false };
  }
};
</script>

<template>
  <div class="space-y-4">
    <!-- Check number -->
    <div class="p-4 rounded-lg border border-n-weak bg-n-background">
      <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_TITLE') }}
      </h4>
      <p class="text-xs text-n-slate-11 mb-3">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_HELP') }}
      </p>
      <div class="flex gap-2">
        <input
          v-model="checkPhone"
          type="tel"
          placeholder="5527999000000"
          class="flex-1 rounded-md border-0 px-3 py-1.5 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-blue-9 sm:text-sm bg-n-background outline-none font-mono"
        />
        <NextButton
          blue
          ghost
          :is-loading="isLoading.check"
          @click="handleCheckNumber"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_BUTTON') }}
        </NextButton>
      </div>
      <div
        v-if="checkResult"
        class="mt-3 p-3 rounded-md text-sm"
        :class="
          checkResult.exists
            ? 'bg-n-teal-3 text-n-teal-12'
            : 'bg-n-amber-3 text-n-amber-12'
        "
      >
        <div class="font-semibold">
          {{
            checkResult.exists
              ? $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_FOUND')
              : $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CHECK_NOT_FOUND')
          }}
        </div>
        <div v-if="checkResult.jid" class="text-xs font-mono mt-1">
          JID: {{ checkResult.jid }}
        </div>
        <div v-if="checkResult.lid" class="text-xs font-mono">
          LID: {{ checkResult.lid }}
        </div>
      </div>
    </div>

    <!-- Contact status (recado) -->
    <div class="p-4 rounded-lg border border-n-weak bg-n-background">
      <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.CONTACT_STATUS_TITLE') }}
      </h4>
      <div class="flex gap-2">
        <input
          v-model="contactJid"
          type="text"
          placeholder="5527999000000@s.whatsapp.net"
          class="flex-1 rounded-md border-0 px-3 py-1.5 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-blue-9 sm:text-sm bg-n-background outline-none font-mono text-xs"
        />
        <NextButton
          blue
          ghost
          :is-loading="isLoading.contactStatus"
          @click="handleContactStatus"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.FETCH_BUTTON') }}
        </NextButton>
      </div>
      <div
        v-if="contactStatus"
        class="mt-3 p-3 rounded-md bg-n-alpha-2 text-sm text-n-slate-12 italic"
      >
        {{
          contactStatus.status ||
          $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.NO_STATUS_SET')
        }}
      </div>
    </div>

    <!-- Block / Unblock + Blocklist -->
    <div class="p-4 rounded-lg border border-n-weak bg-n-background">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCKLIST_TITLE') }}
        </h4>
        <NextButton
          ghost
          icon="i-lucide-refresh-cw"
          sm
          :is-loading="isLoading.blocklist"
          @click="fetchBlocklist"
        />
      </div>
      <div v-if="canManage" class="flex gap-2 mb-3">
        <input
          v-model="blockJid"
          type="text"
          placeholder="5527999000000@s.whatsapp.net"
          class="flex-1 rounded-md border-0 px-3 py-1.5 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-ruby-9 sm:text-sm bg-n-background outline-none font-mono text-xs"
        />
        <NextButton
          ruby
          ghost
          :is-loading="isLoading.block"
          :disabled="!blockJid.trim()"
          @click="handleBlock('block')"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCK_BUTTON') }}
        </NextButton>
        <NextButton
          blue
          ghost
          :is-loading="isLoading.block"
          :disabled="!blockJid.trim()"
          @click="handleBlock('unblock')"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.UNBLOCK_BUTTON') }}
        </NextButton>
      </div>
      <div
        v-if="!blocklistLoaded"
        class="text-xs text-n-slate-11 italic text-center py-3"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCKLIST_HINT') }}
      </div>
      <div
        v-else-if="blocklist.length === 0"
        class="text-xs text-n-slate-11 italic text-center py-3"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DIAG.BLOCKLIST_EMPTY') }}
      </div>
      <ul v-else class="text-xs font-mono space-y-1 max-h-48 overflow-auto">
        <li
          v-for="jid in blocklist"
          :key="jid"
          class="p-1.5 rounded bg-n-alpha-2 text-n-slate-12 truncate"
        >
          {{ jid }}
        </li>
      </ul>
    </div>
  </div>
</template>
